import { supabase } from "@/integrations/supabase/client";
import { emitTrackingDiag, categoriseTrackingError } from "@/lib/trackingDiagnostics";
import { GpsRetryQueue, type QueuedFix } from "@/lib/gpsWritePolicy";

/**
 * Persists driver GPS fixes (drivers row + driver_location_history) with a
 * bounded retry queue. Shared by native and mobile-web tracking.
 *
 *  - Live writes are attempted immediately; failures go to the retry queue.
 *  - History rows keep the fix's original recorded_at (chronological).
 *  - The drivers row is only moved forward — an older retried fix never
 *    overwrites a newer position.
 *  - Retried fixes do not set is_active (prevents reviving a stopped driver).
 */
export interface DriverLocationWriter {
  /** Returns true when the live write succeeded, false when it was queued/skipped. */
  write(lat: number, lng: number): Promise<boolean>;
  clear(): void;
  readonly queueSize: number;
}

export function createDriverLocationWriter(tag: string): DriverLocationWriter {
  let lastRowAt = 0;
  let cachedDriver: { userId: string; id: string; companyId: string | null } | null = null;

  const resolveDriver = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return null;
    if (cachedDriver && cachedDriver.userId === userId) return cachedDriver;
    const { data: driver, error } = await supabase
      .from("drivers")
      .select("id, company_id")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) throw error;
    if (!driver) return null;
    cachedDriver = { userId, id: driver.id, companyId: driver.company_id };
    return cachedDriver;
  };

  const send = async (fix: QueuedFix, live: boolean) => {
    const driver = await resolveDriver();
    if (!driver) return; // signed out / not a driver: nothing to retry
    const recordedIso = new Date(fix.recordedAt).toISOString();

    if (fix.recordedAt >= lastRowAt) {
      const { error } = await supabase
        .from("drivers")
        .update({
          latitude: fix.lat,
          longitude: fix.lng,
          last_location_update: recordedIso,
          ...(live ? { is_active: true } : {}),
        })
        .eq("id", driver.id);
      if (error) throw error;
      lastRowAt = fix.recordedAt;
    }

    const { error: historyError } = await supabase.from("driver_location_history").insert({
      driver_id: driver.id,
      company_id: driver.companyId,
      latitude: fix.lat,
      longitude: fix.lng,
      recorded_at: recordedIso,
    });
    if (historyError) throw historyError;
  };

  const queue = new GpsRetryQueue((fix) => send(fix, false));

  return {
    async write(lat, lng) {
      const fix: QueuedFix = { lat, lng, recordedAt: Date.now() };
      emitTrackingDiag("location_write_attempted", { mode: tag, queue_size: queue.size });
      try {
        await send(fix, true);
        emitTrackingDiag("location_write_succeeded", { mode: tag, queue_size: queue.size });
        if (queue.size > 0) void queue.flush();
        return true;
      } catch (err: any) {
        console.warn(`[${tag}] GPS write failed, queued for retry:`, err?.message || err);
        queue.enqueue(fix);
        emitTrackingDiag("location_write_failed", {
          mode: tag,
          error_category: categoriseTrackingError(err),
          queue_size: queue.size,
        });
        return false;
      }
    },
    clear() {
      queue.clear();
    },
    get queueSize() {
      return queue.size;
    },
  };
}
